import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { GrapesEditor } from '@/components/admin/GrapesEditor'
import { EventsManager, type EventItem } from './EventsManager'
import { router } from '@inertiajs/react'
import { Link } from '@inertiajs/react'
import { ArrowLeft, CheckCircle, Clock, Settings, ExternalLink } from 'lucide-react'
import { useState, useCallback } from 'react'

interface Community {
  id: number
  name: string
}

interface ContentType {
  value: string
  label: string
}

interface PageContentDetail {
  id: number
  page_key: string
  name: string
  description: string | null
  content_type: string
  html_content: string | null
  css_content: string | null
  gjs_data: Record<string, unknown>
  community_id: number | null
  community_name: string | null
  published: boolean
  published_at: string | null
  created_at: string
  updated_at: string
}

interface EditProps {
  page_content: PageContentDetail
  communities: Community[]
  content_types: ContentType[]
  events: EventItem[]
}

const contentTypeLabels: Record<string, string> = {
  banner: 'Banniere',
  footer: 'Footer',
  cta: 'CTA',
  sidebar: 'Sidebar',
  custom: 'Personnalise'
}

export default function Edit({ page_content, events }: EditProps) {
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const isEvent = page_content.content_type === 'events'

  const handleSave = useCallback((data: { html: string; css: string; gjsData: Record<string, unknown> }) => {
    setSaving(true)
    router.patch(
      `/admin/page_contents/${page_content.id}`,
      {
        page_content: {
          html_content: data.html,
          css_content: data.css,
          gjs_data: data.gjsData
        }
      },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          // Show success feedback
          const successDiv = document.createElement('div')
          successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50'
          successDiv.textContent = 'Contenu enregistre !'
          document.body.appendChild(successDiv)
          setTimeout(() => successDiv.remove(), 3000)
        },
        onError: (errors) => {
          console.error('Error saving:', errors)
          const errorDiv = document.createElement('div')
          errorDiv.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50'
          errorDiv.textContent = 'Erreur lors de l\'enregistrement'
          document.body.appendChild(errorDiv)
          setTimeout(() => errorDiv.remove(), 3000)
        },
        onFinish: () => {
          setSaving(false)
        }
      }
    )
  }, [page_content.id])

  const handlePublish = useCallback(() => {
    router.post(`/admin/page_contents/${page_content.id}/publish`, {}, {
      preserveScroll: true
    })
  }, [page_content.id])

  const handleUnpublish = useCallback(() => {
    router.post(`/admin/page_contents/${page_content.id}/unpublish`, {}, {
      preserveScroll: true
    })
  }, [page_content.id])

  const handlePreview = useCallback(() => {
    setShowPreview(true)
  }, [])

  return (
    <AdminLayout
      title={`Editer : ${page_content.name}`}
      breadcrumbs={[
        { label: 'Contenus editables', href: '/admin/page_contents' },
        { label: page_content.name }
      ]}
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link href="/admin/page_contents">
            <Button variant="secondary" className="flex items-center gap-2">
              <ArrowLeft size={18} />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-text-dark">{page_content.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <code className="text-xs bg-gray-100 px-2 py-1 rounded text-text-gray">
                {page_content.page_key}
              </code>
              <span className="text-xs text-text-gray">
                {contentTypeLabels[page_content.content_type] || page_content.content_type}
              </span>
              {page_content.community_name && (
                <span className="text-xs text-text-gray">
                  {page_content.community_name}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link href={`/admin/page_contents/${page_content.id}`}>
            <Button variant="secondary" className="flex items-center gap-2">
              <Settings size={18} />
              Parametres
            </Button>
          </Link>
          {page_content.published ? (
            <Button variant="secondary" onClick={handleUnpublish} className="flex items-center gap-2">
              <Clock size={18} />
              Depublier
            </Button>
          ) : (
            <Button onClick={handlePublish} className="flex items-center gap-2 bg-green-600 hover:bg-green-700">
              <CheckCircle size={18} />
              Publier
            </Button>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <Card className="p-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {page_content.published ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-sm font-medium rounded bg-green-100 text-green-800">
                <CheckCircle size={14} />
                Publie
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-sm font-medium rounded bg-amber-100 text-amber-800">
                <Clock size={14} />
                Brouillon
              </span>
            )}
            <span className="text-sm text-text-gray">
              Derniere modification : {new Date(page_content.updated_at).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
          {page_content.published && (
            <Button
              variant="secondary"
              onClick={handlePreview}
              className="flex items-center gap-2 text-sm"
            >
              <ExternalLink size={14} />
              Voir sur le site
            </Button>
          )}
        </div>
      </Card>

      {/* GrapesJS Editor */}
      <Card className="overflow-hidden" style={{ height: 'calc(100vh - 280px)', minHeight: '600px' }}>
        <GrapesEditor
          initialHtml={page_content.html_content || ''}
          initialCss={page_content.css_content || ''}
          initialGjsData={page_content.gjs_data}
          onSave={handleSave}
          onPreview={handlePreview}
          saving={saving}
        />
      </Card>

      {/* Liste des événements (uniquement pour le type events) */}
      {isEvent && (
        <div className="mt-6">
          <EventsManager pageContentId={page_content.id} initialEvents={events} />
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-text-dark">Apercu</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-text-gray hover:text-text-dark"
              >
                Fermer
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-60px)]">
              {page_content.css_content && (
                <style dangerouslySetInnerHTML={{ __html: page_content.css_content }} />
              )}
              <div dangerouslySetInnerHTML={{ __html: page_content.html_content || '<p class="text-gray-500">Aucun contenu</p>' }} />
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
